import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import type { Extent } from '../lib/extent';
import type { LinearScale } from '../lib/layout';
import { HOUR_MS, MIN_SELECTION_MS, SelectionTarget } from '../lib/selection';
import { useDoubleTap } from '../useDoubleTap';
import styles from './RangeHandles.module.css';

/** Props for {@link RangeHandles}. */
export interface RangeHandlesProps {
  xScale: LinearScale;
  selection: Extent;
  selectable: Extent;
  height: number;
  focusable: boolean;
  valueText: (ms: number) => string;
  onMove: (from: Extent, target: SelectionTarget, deltaMs: number) => void;
  onZoom: () => void;
}

interface Drag {
  target: SelectionTarget;
  fromX: number;
  from: Extent;
}

const GRAB_WIDTH = 32;
const GRIP_WIDTH = 8;
const GRIP_HEIGHT = 24;
const KEY_STEP_HOURS = 1;
const SHIFT_KEY_STEP_HOURS = 6;

const HANDLE_LABELS: Record<
  SelectionTarget.Start | SelectionTarget.End,
  string
> = {
  [SelectionTarget.Start]: 'Selection start',
  [SelectionTarget.End]: 'Selection end',
};

const svgX = (event: PointerEvent<SVGElement>): number =>
  event.clientX -
  (event.currentTarget.ownerSVGElement?.getBoundingClientRect().left ?? 0);

/** The two handles and the band between them; dragging or arrow keys ask `onMove` for a new selection, and double-clicking or double-tapping the band calls `onZoom`. A handle's grab zone never covers the middle half of the band. */
const RangeHandles = ({
  xScale,
  selection,
  selectable,
  height,
  focusable,
  valueText,
  onMove,
  onZoom,
}: RangeHandlesProps) => {
  const drag = useRef<Drag | null>(null);
  const [active, setActive] = useState<SelectionTarget | null>(null);
  const doubleTap = useDoubleTap(onZoom);
  const edges: Extent = [xScale(selection[0]), xScale(selection[1])];
  const inner = Math.min(GRAB_WIDTH / 2, (edges[1] - edges[0]) / 4);
  const limits: Record<SelectionTarget.Start | SelectionTarget.End, Extent> = {
    [SelectionTarget.Start]: [selectable[0], selection[1] - MIN_SELECTION_MS],
    [SelectionTarget.End]: [selection[0] + MIN_SELECTION_MS, selectable[1]],
  };

  const start =
    (target: SelectionTarget) => (event: PointerEvent<SVGElement>) => {
      event.currentTarget.setPointerCapture?.(event.pointerId);
      drag.current = { target, fromX: svgX(event), from: selection };
      setActive(target);
    };
  const follow = (event: PointerEvent<SVGElement>) => {
    const current = drag.current;
    if (!current) return;
    onMove(
      current.from,
      current.target,
      xScale.invert(svgX(event)) - xScale.invert(current.fromX)
    );
  };
  const stop = () => {
    drag.current = null;
    setActive(null);
  };
  const nudge =
    (target: SelectionTarget.Start | SelectionTarget.End) =>
    (event: KeyboardEvent<SVGElement>) => {
      const direction =
        event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
      if (direction === 0) return;
      event.preventDefault();
      const hours = event.shiftKey ? SHIFT_KEY_STEP_HOURS : KEY_STEP_HOURS;
      onMove(selection, target, direction * hours * HOUR_MS);
    };
  const pointer = (target: SelectionTarget) => ({
    onPointerDown: start(target),
    onPointerMove: follow,
    onPointerUp: stop,
    onPointerCancel: stop,
  });

  return (
    <g className={styles.handles} data-dragging={active ?? undefined}>
      <rect
        className={styles.band}
        data-testid='selection-band'
        x={edges[0]}
        y={0}
        width={Math.max(0, edges[1] - edges[0])}
        height={height}
        {...pointer(SelectionTarget.Band)}
        onPointerDown={(event) => {
          start(SelectionTarget.Band)(event);
          doubleTap.down(event);
        }}
        onPointerUp={(event) => {
          stop();
          doubleTap.up(event);
        }}
      />
      {([SelectionTarget.Start, SelectionTarget.End] as const).map(
        (target, i) => {
          const x = edges[i];
          return (
            <g
              key={target}
              className={styles.handle}
              data-active={active === target || undefined}
              role={focusable ? 'slider' : undefined}
              aria-label={focusable ? HANDLE_LABELS[target] : undefined}
              aria-hidden={focusable ? undefined : true}
              aria-valuemin={focusable ? limits[target][0] : undefined}
              aria-valuemax={focusable ? limits[target][1] : undefined}
              aria-valuenow={focusable ? selection[i] : undefined}
              aria-valuetext={focusable ? valueText(selection[i]) : undefined}
              tabIndex={focusable ? 0 : undefined}
              onKeyDown={focusable ? nudge(target) : undefined}
              {...pointer(target)}
            >
              <rect
                className={styles.grab}
                x={
                  target === SelectionTarget.Start
                    ? x - GRAB_WIDTH / 2
                    : x - inner
                }
                y={0}
                width={GRAB_WIDTH / 2 + inner}
                height={height}
              />
              <line className={styles.line} x1={x} x2={x} y1={0} y2={height} />
              <rect
                className={styles.grip}
                x={x - GRIP_WIDTH / 2}
                y={(height - GRIP_HEIGHT) / 2}
                width={GRIP_WIDTH}
                height={GRIP_HEIGHT}
                rx={GRIP_WIDTH / 2}
              />
            </g>
          );
        }
      )}
    </g>
  );
};

export default RangeHandles;
